// FILE: src/api/adminDriverComplianceApi.js
import { adminJson, adminApi } from "./adminHttp";

/* =============================================================================
   Helpers
============================================================================= */

function enc(v) {
  return encodeURIComponent(String(v ?? ""));
}

function getAdminMe() {
  try {
    const raw = localStorage.getItem("admin_me");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function withAdminId(path) {
  const me = getAdminMe();
  const adminId = me?.id;
  if (!adminId) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}adminId=${enc(adminId)}`;
}

function filenameFromContentDisposition(cd) {
  if (!cd) return "";
  const mStar = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (mStar && mStar[1]) {
    try {
      return decodeURIComponent(mStar[1].replace(/"/g, "").trim());
    } catch {
      return mStar[1].replace(/"/g, "").trim();
    }
  }
  const m = cd.match(/filename\s*=\s*"([^"]+)"/i) || cd.match(/filename\s*=\s*([^;]+)/i);
  return m && m[1] ? String(m[1]).replace(/"/g, "").trim() : "";
}

async function readErrorMessage(res) {
  try {
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    if (ct.includes("application/json")) {
      const j = await res.json().catch(() => null);
      if (j?.message) return String(j.message);
      if (j?.error) return String(j.error);
      return JSON.stringify(j);
    }
  } catch {}
  const text = await res.text().catch(() => "");
  return text || `File request failed (${res.status})`;
}

async function fetchFileBlob(path) {
  if (!path) {
    const err = new Error("No file url found");
    err.status = 400;
    throw err;
  }

  // cache buster
  const url = path + (path.includes("?") ? "&" : "?") + "_t=" + Date.now();
  const res = await adminApi(url, { method: "GET" });

  if (!res.ok) {
    const msg = await readErrorMessage(res);
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }

  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") || "";
  const filename = filenameFromContentDisposition(cd) || "file";
  return { blob, filename };
}

/* =============================================================================
   Core endpoints (must match backend)
   Base: /admin/driver-compliance
============================================================================= */

export function listDriverComplianceQueue({ status, bucket, q, page = 0, size = 25 } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (bucket) params.set("bucket", bucket);
  if (q) params.set("q", q);
  params.set("page", String(page));
  params.set("size", String(size));
  return adminJson(`/admin/driver-compliance/queue?${params.toString()}`);
}

export function getDriverComplianceDetail(driverId) {
  return adminJson(`/admin/driver-compliance/${enc(driverId)}`);
}

export function approveDriverCompliance(driverId, body = {}) {
  return adminJson(`/admin/driver-compliance/${enc(driverId)}/approve`, {
    method: "POST",
    body: JSON.stringify(body ?? {}),
  });
}

export function cancelDriverCompliance(driverId, body = {}) {
  return adminJson(`/admin/driver-compliance/${enc(driverId)}/cancel`, {
    method: "POST",
    body: JSON.stringify(body ?? {}),
  });
}

export function overrideApproveDriverCompliance(driverId, body = {}) {
  return adminJson(`/admin/driver-compliance/${enc(driverId)}/override-approve`, {
    method: "POST",
    body: JSON.stringify(body ?? {}),
  });
}

export function forceVerifyDriverCompliance(driverId, body = {}) {
  return adminJson(withAdminId(`/admin/driver-compliance/${enc(driverId)}/force-verify`), {
    method: "POST",
    body: JSON.stringify(body ?? {}),
  });
}

export function requestDriverComplianceCorrection(driverId, payload) {
  return adminJson(`/admin/driver-compliance/${enc(driverId)}/request-correction`, {
    method: "POST",
    body: JSON.stringify(payload ?? {}),
  });
}

export function unlockReuploadDriverCompliance(driverId, payload) {
  return adminJson(`/admin/driver-compliance/${enc(driverId)}/unlock-reupload`, {
    method: "POST",
    body: JSON.stringify(payload ?? {}),
  });
}

export function recomputeDriverCompliance(driverId) {
  return adminJson(`/admin/driver-compliance/${enc(driverId)}/recompute`, {
    method: "POST",
  });
}

/* =============================================================================
   Admin file open/download (driver files) — FIXED
   IMPORTANT: Use SAME pattern as vehicle: fetch blob with auth → open/download.
   Also: use fileUrl param (recommended) instead of key.
============================================================================= */

export function buildDriverFileViewPath(fileUrlOrKey) {
  if (!fileUrlOrKey) return "";
  // Prefer matching your working vehicle style:
  return withAdminId(`/admin/drivers/files/view?fileUrl=${enc(fileUrlOrKey)}`);
}

export function buildDriverFileDownloadPath(fileUrlOrKey) {
  if (!fileUrlOrKey) return "";
  return withAdminId(`/admin/drivers/files/download?fileUrl=${enc(fileUrlOrKey)}`);
}

export async function openAdminDriverFile(fileUrlOrKey) {
  const path = buildDriverFileViewPath(fileUrlOrKey);
  const { blob } = await fetchFileBlob(path);

  const objUrl = URL.createObjectURL(blob);
  window.open(objUrl, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
}

export async function downloadAdminDriverFile(fileUrlOrKey) {
  const path = buildDriverFileDownloadPath(fileUrlOrKey);
  const { blob, filename } = await fetchFileBlob(path);

  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename || "file";
  document.body.appendChild(a);
  a.click();
  a.remove();

  setTimeout(() => URL.revokeObjectURL(objUrl), 10_000);
}
